import * as React from "react";
import { File as FileIcon, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { type UploadConflictFile } from "@/views/drive/modals/uploadModal/types";

interface Props {
  files: readonly UploadConflictFile[];
  onConfirm: (relativePaths: ReadonlySet<string>) => void;
  onCancel: () => void;
}

export const UploadExistsFilesConfirmationModal: React.FC<Props> = ({ files, onConfirm, onCancel }) => {
  const [selectedFiles, setSelectedFiles] = React.useState(files);

  const removeFile = React.useCallback((clientId: string) => {
    setSelectedFiles(currentFiles => currentFiles.filter(file => file.clientId !== clientId));
  }, []);

  const handleConfirm = React.useCallback(() => {
    onConfirm(new Set(selectedFiles.map(file => file.relativePath)));
  }, [onConfirm, selectedFiles]);

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Warning some file exists</DialogTitle>
        </DialogHeader>
        <div className="flex flex-grow flex-col space-y-4 overflow-hidden">
          <p className="text-md m-2 text-gray-500">
            Following files already exist in the folder. Do you want to upload them?
          </p>
          <div className="max-h-[220px] overflow-y-auto">
            {selectedFiles.map(file => (
              <div key={file.clientId} className="flex items-center justify-between gap-4 py-1">
                <div className="flex min-w-0 items-center font-medium">
                  <FileIcon className="mr-2 h-4 w-4 shrink-0" />
                  <span className="truncate" title={file.relativePath}>
                    {file.relativePath}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(file.clientId)}
                  aria-label={`Remove ${file.relativePath}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex justify-between">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedFiles.length === 0}>
            Upload
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
